'use client';

import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function PrivacyPolicyPage() {
  return (
    <>
      <Header />
      <main className="bg-gray-950 min-h-screen pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Privacy Policy</h1>
            <p className="text-gray-400 leading-relaxed">
              E-GREGOR is committed to protecting your privacy. This notice describes how we collect
              and use your personal data. It also describes the rights you have and control you can
              exercise in relation to it.
            </p>
            <p className="text-gray-500 mt-3 text-sm">
              This Privacy Notice was last updated on April 1, 2026.
            </p>
          </div>

          <div className="space-y-10 text-gray-300">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Introduction</h2>
              <p className="leading-relaxed mb-3">
                This Privacy Notice explains how E-GREGOR (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;) collects, uses, and protects
                personal data when you use our website, services, or interact with us.
              </p>
              <p className="leading-relaxed mb-3">
                We are committed to protecting your personal data in accordance with the{' '}
                <strong className="text-white">UK General Data Protection Regulation (UK GDPR)</strong> and the{' '}
                <strong className="text-white">Data Protection Act 2018</strong>.
              </p>
              <p className="leading-relaxed mb-2">
                If you have any questions about E-GREGOR&apos;s use of your personal data, please use the
                following contact:
              </p>
              <ul className="space-y-1 pl-4 text-gray-400">
                <li>
                  Email:{' '}
                  <a href="mailto:sales@egregor.cc" className="text-blue-400 hover:underline">
                    sales@egregor.cc
                  </a>
                </li>
                <li>Post: BUNYAMIN CELEBE-CEO</li>
                <li>E-GREGOR GROUP LTD</li>
                <li>20 WENLOCK ROAD LONDON ENGLAND N1 7GU</li>
              </ul>
            </section>

            {/* Data Collection and Usage */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Data collection and usage</h2>
              <p className="leading-relaxed mb-4">
                We may collect the following categories of personal data:
              </p>

              <div className="mb-4">
                <h3 className="text-white font-medium mb-2">Information You Provide</h3>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>Name, email address, phone number</li>
                  <li>Company name and job title</li>
                  <li>Account registration details</li>
                  <li>Communication content (emails, support tickets)</li>
                </ul>
              </div>

              <div className="mb-4">
                <h3 className="text-white font-medium mb-2">Automatically Collected Data</h3>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>IP address</li>
                  <li>Browser type and device information</li>
                  <li>Usage data (pages visited, time spent)</li>
                  <li>Cookies and tracking data</li>
                </ul>
              </div>

              <div>
                <h3 className="text-white font-medium mb-2">Third-Party Data</h3>
                <ul className="list-disc list-inside space-y-1 pl-2 text-gray-400">
                  <li>Data from analytics providers (e.g., Google Analytics)</li>
                  <li>Data from business partners (if applicable)</li>
                </ul>
              </div>
            </section>

            {/* Your Communications with Us */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Your communications with us</h2>
              <p className="leading-relaxed">
                E-GREGOR may also collect information that you choose to provide in communications with
                us. Please do not send us confidential information until we have confirmed in writing that
                we represent or act for you or your company or organisation. Unsolicited emails from
                non-clients do not establish a lawyer-client relationship. They may not be privileged and,
                therefore, may be disclosed to others.
              </p>
            </section>

            {/* How We Use Your Personal Data */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">How we use your personal data</h2>
              <p className="leading-relaxed mb-3">
                We process personal data for the following purposes:
              </p>
              <ul className="list-disc list-inside space-y-2 pl-2 text-gray-400">
                <li>To provide and manage our IT and cybersecurity services</li>
                <li>To respond to inquiries and provide customer support</li>
                <li>To improve our website and services</li>
                <li>To ensure security and prevent fraud</li>
                <li>To comply with legal obligations</li>
                <li>To send service-related communications</li>
              </ul>
            </section>

            {/* Our Reasons for Using Your Personal Data */}
            <section>
              <h2 className="text-xl font-semibold text-blue-400 mb-3">Our reasons for using your personal data</h2>
              <p className="leading-relaxed mb-4">
                We will process your personal data for a number of reasons:
              </p>
              <ul className="space-y-3 pl-2 text-gray-400">
                <li>
                  <span className="text-white font-semibold">you have given us consent</span>: for example
                  where you share details for particular purposes;
                </li>
                <li>
                  <span className="text-white font-semibold">
                    this is necessary to comply with legal or regulatory obligations
                  </span>
                  : for example anti-money laundering and mandatory client screening checks or disclosure
                  to law enforcement.
                </li>
                <li>this is necessary to deal with legal claims</li>
              </ul>
            </section>
          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-zinc-800">
            <Link href="/" className="text-blue-400 hover:text-blue-300 transition-colors text-sm">
              ← Back to Home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
